import { useState, useEffect, useCallback } from 'react';
import { useWeeklyFolders } from '@/contexts/WeeklyFolderContext';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import styles from './CommentaryPage.module.css';

interface Commentary {
  id: number;
  title: string;
  status: 'draft' | 'review' | 'approved' | 'published';
  generated_version: string;
  edited_version: string | null;
  final_version: string | null;
  user_prompt: string;
  sources_used: {
    permanent_documents: number;
    weekly_documents: number;
    feedback_entries: number;
    previous_commentary: boolean;
  };
  created_at: string;
  updated_at: string;
}

interface Template {
  id: number;
  name: string;
  description: string;
  is_default: boolean;
}

export function CommentaryPage() {
  const { selectedFolder } = useWeeklyFolders();
  const { isEditor } = useAuth();
  const [commentaries, setCommentaries] = useState<Commentary[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedCommentary, setSelectedCommentary] = useState<Commentary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generation form state
  const [selectedTemplate, setSelectedTemplate] = useState<number | ''>('');
  const [userPrompt, setUserPrompt] = useState('');
  const [includePrevious, setIncludePrevious] = useState(true);
  const [showGenerateForm, setShowGenerateForm] = useState(false);

  // Editor state
  const [editedContent, setEditedContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const loadData = useCallback(async () => {
    if (!selectedFolder) return;

    setIsLoading(true);
    setError(null);

    try {
      const [commentaryResponse, templateResponse] = await Promise.all([
        api.get<Commentary[]>(`/commentaries?weekly_folder_id=${selectedFolder.id}`),
        api.get<Template[]>('/templates'),
      ]);

      if (commentaryResponse.success && commentaryResponse.data) {
        setCommentaries(commentaryResponse.data);
      }

      if (templateResponse.success && templateResponse.data) {
        setTemplates(templateResponse.data);
        const defaultTemplate = templateResponse.data.find((t) => t.is_default);
        if (defaultTemplate) {
          setSelectedTemplate(defaultTemplate.id);
        }
      }
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedFolder]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFolder || !selectedTemplate || !userPrompt.trim()) return;

    setIsGenerating(true);
    setError(null);

    try {
      const response = await api.post<Commentary>('/commentary/generate', {
        weekly_folder_id: selectedFolder.id,
        template_id: selectedTemplate,
        user_prompt: userPrompt,
        include_previous: includePrevious,
      });

      if (response.success && response.data) {
        setCommentaries((prev) => [response.data!, ...prev]);
        setSelectedCommentary(response.data);
        setShowGenerateForm(false);
        setUserPrompt('');
      } else {
        setError(response.error || 'Failed to generate commentary');
      }
    } catch (err) {
      setError('Failed to generate commentary');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedCommentary) return;

    try {
      const response = await api.put(`/commentaries/${selectedCommentary.id}`, {
        edited_version: editedContent,
      });

      if (response.success) {
        setCommentaries((prev) =>
          prev.map((c) =>
            c.id === selectedCommentary.id ? { ...c, edited_version: editedContent } : c
          )
        );
        setSelectedCommentary((prev) =>
          prev ? { ...prev, edited_version: editedContent } : null
        );
        setIsEditing(false);
      } else {
        setError(response.error || 'Failed to save changes');
      }
    } catch (err) {
      setError('Failed to save changes');
    }
  };

  const handleStatusChange = async (newStatus: Commentary['status']) => {
    if (!selectedCommentary) return;

    try {
      const updates: { status: Commentary['status']; final_version?: string } = { status: newStatus };

      if (newStatus === 'published') {
        updates.final_version =
          selectedCommentary.edited_version || selectedCommentary.generated_version;
      }

      const response = await api.put(`/commentaries/${selectedCommentary.id}`, updates);

      if (response.success) {
        setCommentaries((prev) =>
          prev.map((c) => (c.id === selectedCommentary.id ? { ...c, ...updates } : c))
        );
        setSelectedCommentary((prev) => (prev ? { ...prev, ...updates } : null));
      } else {
        setError(response.error || 'Failed to update status');
      }
    } catch (err) {
      setError('Failed to update status');
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: Commentary['status']) => {
    const statusMap = {
      draft: 'info',
      review: 'warning',
      approved: 'success',
      published: 'primary',
    };
    return `badge badge-${statusMap[status]}`;
  };

  const getCurrentContent = (commentary: Commentary) => {
    return commentary.final_version || commentary.edited_version || commentary.generated_version;
  };

  if (!selectedFolder) {
    return (
      <div className={styles.container}>
        <div className={styles.empty}>
          <p>Please select a weekly folder to view commentaries</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>Commentary</h1>
          <p className={styles.subtitle}>
            Commentaries for {selectedFolder.title}
          </p>
        </div>

        {isEditor && (
          <button
            className="btn btn-primary"
            onClick={() => setShowGenerateForm(!showGenerateForm)}
          >
            {showGenerateForm ? 'Cancel' : 'Generate New'}
          </button>
        )}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {/* Generate Form */}
      {showGenerateForm && (
        <div className={styles.generateForm}>
          <h2>Generate Commentary</h2>
          <form onSubmit={handleGenerate}>
            <div className="form-group">
              <label className="form-label">Template</label>
              <select
                className="form-select"
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(parseInt(e.target.value))}
                required
              >
                <option value="">Select a template</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} {t.is_default ? '(Default)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Your Instructions</label>
              <textarea
                className="form-textarea"
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
                placeholder="Provide specific instructions for this commentary..."
                rows={4}
                required
              />
            </div>

            <div className={styles.checkboxGroup}>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={includePrevious}
                  onChange={(e) => setIncludePrevious(e.target.checked)}
                />
                <span>Include previous commentary for consistency</span>
              </label>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={isGenerating || !selectedTemplate || !userPrompt.trim()}
            >
              {isGenerating ? (
                <>
                  <span className="spinner" />
                  Generating...
                </>
              ) : (
                'Generate Commentary'
              )}
            </button>
          </form>
        </div>
      )}

      <div className={styles.content}>
        {/* Commentary List */}
        <div className={styles.sidebar}>
          <h3>Commentaries</h3>
          {isLoading ? (
            <div className={styles.loading}>
              <div className="spinner" />
            </div>
          ) : commentaries.length === 0 ? (
            <p className={styles.emptyList}>No commentaries yet</p>
          ) : (
            <div className={styles.list}>
              {commentaries.map((c) => (
                <button
                  key={c.id}
                  className={`${styles.listItem} ${
                    selectedCommentary?.id === c.id ? styles.listItemActive : ''
                  }`}
                  onClick={() => {
                    setSelectedCommentary(c);
                    setEditedContent(getCurrentContent(c));
                    setIsEditing(false);
                  }}
                >
                  <div className={styles.listItemTitle}>{c.title}</div>
                  <div className={styles.listItemMeta}>
                    <span className={getStatusBadge(c.status)}>{c.status}</span>
                    <span>{formatDate(c.created_at)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Commentary View/Edit */}
        <div className={styles.main}>
          {selectedCommentary ? (
            <>
              <div className={styles.commentaryHeader}>
                <h2>{selectedCommentary.title}</h2>
                <div className={styles.commentaryMeta}>
                  <span className={getStatusBadge(selectedCommentary.status)}>
                    {selectedCommentary.status}
                  </span>
                  <span className={styles.sources}>
                    {selectedCommentary.sources_used.permanent_documents +
                      selectedCommentary.sources_used.weekly_documents}{' '}
                    documents used
                  </span>
                </div>
              </div>

              {isEditing ? (
                <div className={styles.editor}>
                  <textarea
                    className={styles.editorTextarea}
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    rows={20}
                  />
                  <div className={styles.editorActions}>
                    <button className="btn btn-primary" onClick={handleSaveEdit}>
                      Save Changes
                    </button>
                    <button
                      className="btn btn-outline"
                      onClick={() => {
                        setIsEditing(false);
                        setEditedContent(getCurrentContent(selectedCommentary));
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className={styles.commentaryContent}>
                    <pre>{getCurrentContent(selectedCommentary)}</pre>
                  </div>

                  {isEditor && (
                    <div className={styles.actions}>
                      <button
                        className="btn btn-outline"
                        onClick={() => setIsEditing(true)}
                      >
                        Edit
                      </button>
                      {selectedCommentary.status === 'draft' && (
                        <button
                          className="btn btn-secondary"
                          onClick={() => handleStatusChange('review')}
                        >
                          Send for Review
                        </button>
                      )}
                      {selectedCommentary.status === 'review' && (
                        <>
                          <button
                            className="btn btn-outline"
                            onClick={() => handleStatusChange('draft')}
                          >
                            Back to Draft
                          </button>
                          <button
                            className="btn btn-success"
                            onClick={() => handleStatusChange('approved')}
                          >
                            Approve
                          </button>
                        </>
                      )}
                      {selectedCommentary.status === 'approved' && (
                        <button
                          className="btn btn-primary"
                          onClick={() => handleStatusChange('published')}
                        >
                          Publish
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <div className={styles.noSelection}>
              <p>Select a commentary to view</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
